/** Inventur per Kamera (MP-C): im Laden alle Etiketten im eigenen Regal scannen – was fehlt, meldet das Portal; Schwund an den Laden melden. */
import { useTx } from "../i18n";
import { useRef, useState } from "react";
import { Link } from "react-router";
import { Button, EmptyState, Icon, Notice, Seitenzustand, StatusBadge } from "../ui";
import { ItemRow } from "../ui/ItemRow";
import { ApiFehler } from "../api/client";
import { parseArtikelCode } from "@shared/code";
import { useKameraScanner, useTastaturScanner } from "../kasse/scanner";
import { fotoUrl, useMeineArtikel } from "./artikel-api";
import { useFeedbackSenden } from "./portal-c-api";

export function Inventur() {
  const tx = useTx();
  const q = useMeineArtikel("im_regal");
  const feedback = useFeedbackSenden();
  const video = useRef<HTMLVideoElement>(null);
  const [aktiv, setAktiv] = useState(false);
  const [gesehen, setGesehen] = useState<Set<string>>(new Set());
  const [fremd, setFremd] = useState<string[]>([]);
  const [letzter, setLetzter] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const onCode = (roh: string) => {
    const p = parseArtikelCode(roh);
    if (!p) return;
    const eigen = q.data?.artikel.find((a) => a.code === p.code);
    if (eigen) {
      setGesehen((s) => new Set(s).add(eigen.id));
      setLetzter(`${eigen.bezeichnung} ✓`);
      if (navigator.vibrate) navigator.vibrate(30);
    } else {
      setFremd((f) => (f.includes(p.code) ? f : [...f, p.code]));
      setLetzter(`${p.code} – nicht von dir`);
    }
  };
  const status = useKameraScanner(video, aktiv, onCode);
  useTastaturScanner(true, onCode);
  const liste = q.data?.artikel ?? [];
  const fehlend = liste.filter((a) => !gesehen.has(a.id));

  return (
    <div className="sc-col" style={{ gap: "var(--space-3)" }}>
      <div className="sc-row">
        <Link to="/mieter/regal" className="rg-btn rg-btn--quiet" aria-label="Zurück">‹</Link>
        <h1 className="title">{tx("Inventur")}</h1>
      </div>
      <p className="sc-muted">{tx("Im Laden alle Etiketten in deinem Regal scannen. Was danach fehlt, ist verkauft, in der Rückgabe-Kiste – oder weg.")}</p>
      {meldung ? <Notice tone="success">{meldung}</Notice> : null}
      <div className="sc-camera" style={{ borderRadius: "var(--radius-lg)", overflow: "hidden", background: "var(--surface-sunken)", aspectRatio: "4 / 3", position: "relative" }}>
        <video ref={video} playsInline muted style={{ width: "100%", height: "100%", objectFit: "cover", display: aktiv ? "block" : "none" }} />
        {!aktiv ? (
          <div className="sc-col sc-center" style={{ position: "absolute", inset: 0, justifyContent: "center", gap: "var(--space-2)" }}>
            <Icon name="scan-line" size={40} />
            <Button variant="primary" icon="camera" onClick={() => setAktiv(true)}>Kamera starten</Button>
            <span className="sc-muted sc-small">Oder Bluetooth-Scanner benutzen</span>
          </div>
        ) : null}
        {aktiv && status === "verweigert" ? <Notice tone="attention">Kamera nicht erlaubt – in den Browser-Einstellungen freigeben.</Notice> : null}
        {letzter ? <span className="sc-pill" style={{ position: "absolute", left: 8, bottom: 8 }}>{letzter}</span> : null}
      </div>
      <div className="sc-spread">
        <StatusBadge status="paid">{gesehen.size} gefunden</StatusBadge>
        <StatusBadge status={fehlend.length ? "attention" : "paid"}>{fehlend.length} fehlen</StatusBadge>
        {fremd.length ? <StatusBadge status="neutral">{fremd.length} fremde</StatusBadge> : null}
        {aktiv ? <Button variant="quiet" onClick={() => setAktiv(false)}>Kamera aus</Button> : null}
      </div>
      <Seitenzustand query={q} lines={4} thumb istLeer={(d) => d.artikel.length === 0} leer={<EmptyState illustration="empty-shelf" title={tx("Nichts im Regal")}>Erst Artikel erfassen, dann lässt sich zählen.</EmptyState>}>
        {() => (
          <>
            {gesehen.size > 0 && fehlend.length ? (
              <>
                <h2 className="heading">{tx("Noch nicht gesehen")}</h2>
                <div>{fehlend.map((a) => <ItemRow key={a.id} title={a.bezeichnung} photo={fotoUrl(a.fotoKey)} meta={a.code} price={a.preisCent} tone="attention" />)}</div>
                <Button variant="secondary" icon="mail" loading={feedback.isPending} onClick={() => feedback.mutate({ an: "laden", text: `Inventur: ${fehlend.length} Artikel nicht im Regal gefunden – ${fehlend.map((a) => a.code).join(", ")}` }, { onSuccess: () => setMeldung("An den Laden gemeldet – er prüft Kasse und Rückgabe-Kiste."), onError: (e) => setMeldung(e instanceof ApiFehler ? e.message : "Melden hat nicht geklappt.") })}>Fehlende dem Laden melden</Button>
              </>
            ) : gesehen.size > 0 && fehlend.length === 0 ? (
              <Notice tone="joy" icon="sparkles" title={tx("Alles da")}>Alle {liste.length} Artikel gefunden – dein Regal ist vollständig.</Notice>
            ) : null}
            {fremd.length ? <p className="sc-muted sc-small">Fremde Codes im Regal: {fremd.join(", ")} – gehören einem anderen Mieter, bitte dem Laden sagen.</p> : null}
          </>
        )}
      </Seitenzustand>
    </div>
  );
}
